// RFC 9457 problem+json and the complete error taxonomy (modules-and-api.md §0.4, §2.10).
// Nothing outside this table may reach a client. The problem-type base is the product domain (D34: vision-x.co.za).
export const PROBLEM_BASE = "https://vision-x.co.za/problems/";

export const PROBLEMS = {
  VALIDATION_FAILED:        { status: 422, title: "Validation failed" },
  NOT_FOUND:                { status: 404, title: "Not found" },
  PERMISSION_DENIED:        { status: 403, title: "Permission denied" },
  STEP_UP_REQUIRED:         { status: 403, title: "Control PIN required" },
  STEP_UP_REFUSED:          { status: 403, title: "Control PIN refused" },
  STEP_UP_LOCKED:           { status: 423, title: "Control ID locked" },
  VERSION_CONFLICT:         { status: 409, title: "The record has changed" },
  DOCUMENT_LOCKED:          { status: 409, title: "Document is being edited" },
  ILLEGAL_TRANSITION:       { status: 409, title: "Not allowed from the current status" },
  IMMUTABLE_RECORD:         { status: 409, title: "Record can no longer be changed" },
  PATIENT_NOT_TRANSACTABLE: { status: 409, title: "Patient account cannot transact" },
  INSUFFICIENT_STOCK:       { status: 409, title: "Not enough stock" },
  SERIAL_UNAVAILABLE:       { status: 409, title: "Serial not available" },
  SEND_VALIDATION_FAILED:   { status: 422, title: "Cannot send yet" },
  IDEMPOTENCY_KEY_REUSED:   { status: 409, title: "Idempotency key already used with a different request" },
  REQUEST_IN_FLIGHT:        { status: 409, title: "The same request is still being processed" },
  REQUEST_COMPLETED:        { status: 409, title: "The same request was already carried out" },
  RATE_LIMITED:             { status: 429, title: "Too many requests" },
  FEATURE_DISABLED:         { status: 404, title: "Not found" },
  PROVIDER_NOT_READY:       { status: 409, title: "The provider connection is not ready" },
  AUTHENTICATION_REQUIRED:  { status: 401, title: "Sign in required" },
  AUTHENTICATION_FAILED:    { status: 401, title: "Sign in failed" },
  ACCOUNT_LOCKED:           { status: 423, title: "Account locked" },
  INTERNAL_ERROR:           { status: 500, title: "Something went wrong" },
} as const;

export type ProblemCode = keyof typeof PROBLEMS;

/**
 * The words of `409 REQUEST_COMPLETED`: a request carried out whose answer was not kept for a replay — too large for its record,
 * refused by the database, or its record not completed and its key marked spent (BR-GOVERNANCE-016). The server's words and the
 * published example's, one source, so the contract never says what the server does not (the verification of round 3's fixes, V5W-3).
 */
export const requestCompletedDetail = (answeredStatus: number): string =>
  `This request was carried out and answered ${answeredStatus}; its answer was not kept for a replay: read the record to see it.`;

/**
 * How long the Worker keeps a request's idempotency record: 24 hours from the first request under its key that reached it, after which
 * the key is free again and a request under it is a new one (modules-and-api.md §2.9; the API contract's "Records are kept 24 h"). One
 * figure for the Worker's middleware and the PWA, whose count screen sends a lost answer's key again only within it, less a margin
 * (UC-INVENTORY-008 A11 step 2; the verification of round 7's fix-ups, 25 Oct 2026, V7IR-4).
 */
export const IDEMPOTENCY_RETENTION_HOURS = 24;

export interface FieldError { path: string; code: string; message: string; }

export interface Problem {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  requestId: string;
  code: ProblemCode;
  errors?: FieldError[];
  meta?: Record<string, unknown>;
}

export class ProblemError extends Error {
  readonly code: ProblemCode;
  readonly status: number;
  readonly detail: string | undefined;
  readonly errors: FieldError[] | undefined;
  readonly meta: Record<string, unknown> | undefined;
  constructor(code: ProblemCode, opts: { detail?: string; errors?: FieldError[]; meta?: Record<string, unknown> } = {}) {
    super(opts.detail ?? PROBLEMS[code].title);
    this.name = "ProblemError";
    this.code = code;
    this.status = PROBLEMS[code].status;
    this.detail = opts.detail;
    this.errors = opts.errors;
    this.meta = opts.meta;
  }
}

export function toProblem(err: ProblemError, requestId: string, instance?: string): Problem {
  const p: Problem = {
    type: PROBLEM_BASE + err.code.toLowerCase().replace(/_/g, "-"),
    title: PROBLEMS[err.code].title,
    status: err.status,
    requestId,
    code: err.code,
  };
  if (err.detail) p.detail = err.detail;
  if (instance) p.instance = instance;
  if (err.errors) p.errors = err.errors;
  if (err.meta) p.meta = err.meta;
  return p;
}
